import { mdiClose, mdiMagnify, mdiTune, mdiFileDocumentOutline, mdiAccountInjuryOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Popover } from '@mui/material';
import * as React from 'react';
import _ from 'lodash';
import { BaseInput } from '../base/input';
import { DatePicker } from '../base/datepicker';
import { Toggle } from '../base/toggle';
import * as S from './advanced-claim-search-bar.styles';
import {
    CoreFetch,
    HL7Api,
    ListProviderHL7MessagesRequest,
    ListSupportHL7MessagesRequest,
    ProviderHL7Message,
    SupportHL7Message,
    ProviderCreateClaimProfessionalServices,
    ProviderCreateClaimInstitutionalServices,
    SupportCreateClaimProfessionalServices,
    SupportCreateClaimInstitutionalServices,
    ProviderHL7SortType,
    SupportHL7SortType,
    ListProviderHL7MessagesOrderEnum,
    ListSupportHL7MessagesOrderEnum,
} from '../../api';
import { formatDate } from '../../services/primitives/date-utils';

const datePattern = /^\d{4}-\d{2}-\d{2}$/;
const numberPattern = /^\d+$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isDate = (term: string): boolean => {
    return datePattern.exec(term) !== null;
};

const isNumber = (term: string): boolean => {
    return numberPattern.exec(term) !== null;
};

const isUuid = (term: string): boolean => {
    return uuidPattern.exec(term) !== null;
};

const equalsFilter = (term?: string | string[]): string[] | undefined => {
    if (_.isArray(term)) {
        return [_.map(term, d => `EQ:${d}`).join(',')];
    } else if (_.isString(term)) {
        return [`EQ:${term}`];
    }
};

type ServiceType =
    | ProviderCreateClaimProfessionalServices
    | ProviderCreateClaimInstitutionalServices
    | SupportCreateClaimProfessionalServices
    | SupportCreateClaimInstitutionalServices;

interface ISearchFilters {
    filterClaimId?: string[];
    filterClaimControlNumber?: string[];
    filterFromDos?: string[];
    filterPatientExternalId?: string[];
    filterPatientFirstName?: string[];
    filterPatientLastName?: string[];
    filterPatientDob?: string[];
}

// Parse search query and return applicable filters
const parseSearchQuery = (query: string): ISearchFilters => {
    const filters: ISearchFilters = {};
    const trimmedQuery = _.trim(query);

    if (!trimmedQuery) {
        return filters;
    }

    // Check for UUID - claim ID
    if (isUuid(trimmedQuery)) {
        filters.filterClaimId = equalsFilter(trimmedQuery);
        return filters;
    }

    // Check for date - could be fromDos or patientDob
    if (isDate(trimmedQuery)) {
        const dateFilter = equalsFilter(trimmedQuery);
        filters.filterFromDos = dateFilter;
        return filters;
    }

    // Split into tokens
    const tokens = _.compact(_.split(trimmedQuery, ' '));

    // Single token
    if (tokens.length === 1) {
        const token = tokens[0];

        // Mostly digits - likely control number or external ID
        if (isNumber(token)) {
            filters.filterClaimControlNumber = equalsFilter(token);
        } else {
            // Alpha - treat as last name
            filters.filterPatientLastName = equalsFilter(token);
        }
        return filters;
    }

    // Two tokens
    if (tokens.length === 2) {
        const [first, second] = tokens;

        // Check if one is a date
        const firstIsDate = isDate(first);
        const secondIsDate = isDate(second);

        if (firstIsDate || secondIsDate) {
            const dateToken = firstIsDate ? first : second;
            const otherToken = firstIsDate ? second : first;

            filters.filterFromDos = equalsFilter(dateToken);

            // Check if non date token is numeric or alpha
            if (isNumber(otherToken)) {
                // if numeric treat as control number
                filters.filterClaimControlNumber = equalsFilter(otherToken);
            } else {
                // if alpha treat as last name
                filters.filterPatientLastName = equalsFilter(otherToken);
            }
        } else {
            // Both alpha - first name + last name
            filters.filterPatientFirstName = equalsFilter(first);
            filters.filterPatientLastName = equalsFilter(second);
        }
        return filters;
    }

    // Multiple tokens - extract dates, numbers, and names separately
    const dateTokens = _.filter(tokens, isDate);
    const numericTokens = _.filter(tokens, isNumber);
    const alphaTokens = _.filter(tokens, t => !isNumber(t) && !isDate(t));

    // Apply date filter if found
    if (dateTokens.length > 0) {
        const dateFilter = equalsFilter(dateTokens[0]);
        filters.filterFromDos = dateFilter;
    }

    // Apply numeric filters
    if (numericTokens.length > 0) {
        filters.filterClaimControlNumber = equalsFilter(numericTokens[0]);
        filters.filterPatientExternalId = equalsFilter(numericTokens[0]);
    }

    // Apply name filters
    if (alphaTokens.length >= 2) {
        filters.filterPatientFirstName = equalsFilter(alphaTokens[0]);
        filters.filterPatientLastName = equalsFilter(_.tail(alphaTokens).join(' '));
    } else if (alphaTokens.length === 1) {
        filters.filterPatientLastName = equalsFilter(alphaTokens[0]);
    }

    return filters;
};

interface IProcessedClaim {
    claimId: string;
    createdAt: string;
    hl7MessageId: string;
    dos: string;
    controlNumber?: string;
}

interface IProcessedPatient {
    id?: string;
    externalId: string;
    firstName: string;
    lastName: string;
    dob: string;
    claims: IProcessedClaim[];
}

export interface IAdvancedClaimSearchBarProps {
    placeholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    onSubmit?: () => void;
    className?: string;
    isSupport: boolean;
    accountId?: string;
}

interface IAdvancedClaimSearchBarState {
    searchValue: string;
    popoverMode: 'filters' | 'results' | null;
    patientFirstName: string;
    patientLastName: string;
    fromDateOfService: string;
    patientDob: string;
    patientExternalId: string;
    claimControlNumber: string;
    additionalId: string;
    processedResults: IProcessedPatient[];
    isSearching: boolean;
    searchError: string | null;
    openInNewTab: boolean;
    hasSearched: boolean;
    datePickerKey: number;
    currentOffset: number;
    hasMoreResults: boolean;
    isLoadingMore: boolean;
    currentSearchToken: number;
}

export class AdvancedClaimSearchBar extends React.Component<
    IAdvancedClaimSearchBarProps,
    IAdvancedClaimSearchBarState
> {
    private wrapperRef = React.createRef<HTMLDivElement>();
    private scrollContainerRef = React.createRef<HTMLDivElement>();
    private debouncedSearch: _.DebouncedFunc<(query: string) => Promise<void>>;

    constructor(props: IAdvancedClaimSearchBarProps) {
        super(props);
        this.state = {
            searchValue: props.value || '',
            popoverMode: null,
            patientFirstName: '',
            patientLastName: '',
            fromDateOfService: '',
            patientDob: '',
            patientExternalId: '',
            claimControlNumber: '',
            additionalId: '',
            processedResults: [],
            isSearching: false,
            searchError: null,
            openInNewTab: true,
            hasSearched: false,
            datePickerKey: 0,
            currentOffset: 0,
            hasMoreResults: false,
            isLoadingMore: false,
            currentSearchToken: 0,
        };

        this.debouncedSearch = _.debounce(this.performSearch, 1000);
    }

    public componentDidUpdate(prevProps: IAdvancedClaimSearchBarProps, prevState: IAdvancedClaimSearchBarState): void {
        if (prevProps.value !== this.props.value && this.props.value !== this.state.searchValue) {
            this.setState({ searchValue: this.props.value || '' });
        }

        const resultsChanged = prevState.processedResults !== this.state.processedResults;

        if (resultsChanged && this.state.hasMoreResults && !this.state.isLoadingMore) {
            const el = this.scrollContainerRef.current;
            if (el && el.scrollHeight <= el.clientHeight) {
                this.loadMoreResults();
            }
        }
    }

    public componentWillUnmount(): void {
        // Cancel any pending debounced searches
        this.debouncedSearch.cancel();
    }

    private readonly handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
        const target = e.currentTarget;
        const scrollThreshold = 50;

        const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (scrollBottom < scrollThreshold && this.state.hasMoreResults && !this.state.isLoadingMore) {
            this.loadMoreResults();
        }
    };

    private readonly mergePatientResults = (
        existing: IProcessedPatient[],
        newPatients: IProcessedPatient[],
    ): IProcessedPatient[] => {
        // Combine both arrays
        const allPatients = [...existing, ...newPatients];
        // Collect all claims from all patient entries with patient reference
        const allClaimRecords = allPatients.flatMap(patient =>
            patient.claims.map(claim => ({
                claim,
                patient,
            })),
        );
        // Group by claimId
        const groupedByClaim = _.groupBy(allClaimRecords, r => r.claim.claimId);
        // For each claim, keep the latest HL7 (createdAt DESC)
        const uniqueClaimRecords = Object.values(groupedByClaim).map(group => {
            const sorted = _.orderBy(group, [r => r.claim.createdAt], ['desc']);
            return sorted[0]; // latest = source of truth
        });
        // Now each claim has exactly ONE patient (correct owner)
        const claimsByExternalId = new Map<string, IProcessedClaim[]>();
        const patientMetaByExternalId = new Map<string, IProcessedPatient>();
        uniqueClaimRecords.forEach(({ claim, patient }) => {
            const externalId = patient.externalId;
            if (!claimsByExternalId.has(externalId)) {
                claimsByExternalId.set(externalId, []);
            }
            claimsByExternalId.get(externalId)!.push(claim);
            if (!patientMetaByExternalId.has(externalId)) {
                patientMetaByExternalId.set(externalId, patient);
            }
        });
        // Preserve order
        const patientOrder = _.uniq(allPatients.map(p => p.externalId));
        const mergedPatients: IProcessedPatient[] = patientOrder
            .filter(externalId => claimsByExternalId.has(externalId))
            .map(externalId => {
                const patientMeta = patientMetaByExternalId.get(externalId)!;
                const claims = claimsByExternalId.get(externalId)!;
                const sortedClaims = _.orderBy(claims, ['dos'], ['desc']);
                return {
                    ...patientMeta,
                    claims: sortedClaims,
                };
            });
        return mergedPatients;
    };

    private readonly fetchHL7Messages = async (
        filters: ISearchFilters,
        offset: number,
        isSupport: boolean,
    ): Promise<Array<SupportHL7Message | ProviderHL7Message>> => {
        const baseRequest = {
            ...filters,
            limit: 50,
            offset,
        };

        if (isSupport) {
            const request: ListSupportHL7MessagesRequest = {
                ...baseRequest,
                filterActive: 'EQ:1',
                sort: [SupportHL7SortType.PATIENTEXTERNALID],
                order: [ListSupportHL7MessagesOrderEnum.DESCENDING],
            };
            return await CoreFetch<HL7Api>(HL7Api).listSupportHL7Messages(request);
        } else {
            const request: ListProviderHL7MessagesRequest = {
                ...baseRequest,
                sort: [ProviderHL7SortType.PATIENTEXTERNALID],
                order: [ListProviderHL7MessagesOrderEnum.DESCENDING],
            };
            return await CoreFetch<HL7Api>(HL7Api).listProviderHL7Messages(request);
        }
    };

    private readonly loadMoreResults = async (): Promise<void> => {
        if (this.state.isLoadingMore || !this.state.hasMoreResults) {
            return;
        }

        const requestToken = this.state.currentSearchToken;
        const { currentOffset } = this.state;
        const nextOffset = currentOffset + 50;

        this.setState({ isLoadingMore: true });

        try {
            const { isSupport } = this.props;
            const { searchValue } = this.state;

            const filters = this.hasAnyFiltersSet() ? this.buildAdvancedFilters() : parseSearchQuery(searchValue);

            const results = await this.fetchHL7Messages(filters, nextOffset, isSupport);

            // Check if this response is still valid
            if (this.state.currentSearchToken !== requestToken) {
                // Stale response - ignore results but clear loading state
                this.setState({ isLoadingMore: false });
                return;
            }

            const processed = this.processSearchResults(results);

            this.setState(prevState => {
                const mergedResults = this.mergePatientResults(prevState.processedResults, processed);

                return {
                    processedResults: mergedResults,
                    currentOffset: nextOffset,
                    hasMoreResults: results.length === 50,
                    isLoadingMore: false,
                };
            });
        } catch (error) {
            this.setState({
                isLoadingMore: false,
            });
        }
    };

    public render(): React.ReactElement {
        const { placeholder, className } = this.props;
        const { searchValue, popoverMode } = this.state;

        return (
            <S.SearchBarContainer className={className}>
                <S.SearchInputWrapper ref={this.wrapperRef} data-popover-open={popoverMode !== null}>
                    <S.SearchIcon path={mdiMagnify} size={0.75} />
                    <BaseInput
                        value={searchValue}
                        onChange={this.handleSearchChange}
                        onFocus={this.handleInputFocus}
                        placeholder={placeholder || 'Search'}
                        onOption={this.handleFilterButtonClick}
                        optionPath={mdiTune}
                    />
                </S.SearchInputWrapper>

                <Popover
                    anchorEl={this.wrapperRef.current}
                    open={popoverMode !== null}
                    onClose={this.handlePopoverClose}
                    disableRestoreFocus
                    disableEnforceFocus
                    disableAutoFocus
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            style: {
                                borderRadius: '12px',
                                boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
                                marginTop: '8px',
                                maxHeight: 'calc(100vh - 100px)',
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        },
                    }}
                    style={{ zIndex: 9999 }}>
                    {this.renderUnifiedPopoverContent()}
                </Popover>
            </S.SearchBarContainer>
        );
    }

    private readonly renderUnifiedPopoverContent = (): React.ReactElement => {
        const { popoverMode } = this.state;

        if (popoverMode === 'filters') {
            return this.renderPopoverContent();
        } else {
            return <S.PopoverContent>{this.renderSearchResults()}</S.PopoverContent>;
        }
    };

    private readonly renderPopoverContent = (): React.ReactElement => {
        const {
            patientFirstName,
            patientLastName,
            fromDateOfService,
            patientDob,
            patientExternalId,
            claimControlNumber,
            additionalId,
            popoverMode,
            processedResults,
            isSearching,
            searchError,
            hasSearched,
        } = this.state;

        const hasResults = processedResults.length > 0 || isSearching || !!searchError || hasSearched;
        const showResults = popoverMode === 'filters' && hasResults;
        const isSubmitDisabled = !this.hasAnyFiltersSet();

        return (
            <S.PopoverContent>
                <S.PopoverHeader>
                    <S.PopoverTitle>Advanced Search</S.PopoverTitle>
                    <S.CloseButton onClick={this.handlePopoverClose} type="button">
                        <Icon path={mdiClose} size={0.8} />
                    </S.CloseButton>
                </S.PopoverHeader>

                <S.PopoverBody>
                    <S.FormGrid>
                        <S.FormField>
                            <S.Label>Patient First Name</S.Label>
                            <BaseInput
                                value={patientFirstName}
                                onChange={this.handleFieldChange('patientFirstName')}
                                placeholder="Bob"
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Patient Last Name</S.Label>
                            <BaseInput
                                value={patientLastName}
                                onChange={this.handleFieldChange('patientLastName')}
                                placeholder="Smith"
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>From Date of Service</S.Label>
                            <DatePicker
                                key={`fromDateOfService-${this.state.datePickerKey}`}
                                value={fromDateOfService}
                                onDateSelected={this.handleDateChange('fromDateOfService')}
                                inputProps={{ placeholder: 'Jan 1st, 2026' }}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Patient DOB</S.Label>
                            <DatePicker
                                key={`patientDob-${this.state.datePickerKey}`}
                                value={patientDob}
                                onDateSelected={this.handleDateChange('patientDob')}
                                inputProps={{ placeholder: 'Jan 1st, 2026' }}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Patient External ID</S.Label>
                            <BaseInput
                                value={patientExternalId}
                                onChange={this.handleFieldChange('patientExternalId')}
                                placeholder="123456789"
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Claim Control Number</S.Label>
                            <BaseInput
                                value={claimControlNumber}
                                onChange={this.handleFieldChange('claimControlNumber')}
                                placeholder="123456789"
                            />
                        </S.FormField>

                        <S.FormFieldFullWidth>
                            <S.Label>ENTER Claim ID</S.Label>
                            <BaseInput
                                value={additionalId}
                                onChange={this.handleFieldChange('additionalId')}
                                placeholder="123456789"
                            />
                        </S.FormFieldFullWidth>
                    </S.FormGrid>
                </S.PopoverBody>

                <S.PopoverFooter>
                    <S.ClearButton onClick={this.handleClear}>Clear</S.ClearButton>
                    <S.SubmitButton onClick={this.handleSubmit} disabled={isSubmitDisabled}>
                        Submit
                    </S.SubmitButton>
                </S.PopoverFooter>

                {showResults && <S.Divider />}
                {showResults && this.renderSearchResults()}
            </S.PopoverContent>
        );
    };

    private readonly getPatientFromMessage = (msg: ProviderHL7Message | SupportHL7Message) => {
        const providerMsg = msg as ProviderHL7Message;
        if (providerMsg.professional?.patient) return providerMsg.professional.patient;
        if (providerMsg.institutional?.patient) return providerMsg.institutional.patient;

        const supportMsg = msg as SupportHL7Message;
        if (supportMsg.supportProfessional?.patient) return supportMsg.supportProfessional.patient;
        if (supportMsg.providerProfessional?.patient) return supportMsg.providerProfessional.patient;
        if (supportMsg.supportInstitutional?.patient) return supportMsg.supportInstitutional.patient;
        if (supportMsg.providerInstitutional?.patient) return supportMsg.providerInstitutional.patient;

        return null;
    };

    private readonly processSearchResults = (
        results: Array<ProviderHL7Message | SupportHL7Message>,
    ): IProcessedPatient[] => {
        const messagesWithClaims = results.filter(msg => {
            if (!msg.claimId) return false;
            const patient = this.getPatientFromMessage(msg);
            return patient?.externalId != null;
        });
    
        // Group by claimId FIRST (source of truth)
        const groupedByClaim = _.groupBy(messagesWithClaims, msg => msg.claimId);
    
        // For each claim, keep the latest HL7 (createdAt DESC)
        const uniqueClaimRecords = Object.values(groupedByClaim).map(messages => {
            const sorted = _.orderBy(messages, [m => m.created || ''], ['desc']); // latest; undated treated as oldest
            return sorted[0];
        });

        // Now each claim has exactly ONE patient (correct owner)
        const claimsByExternalId = new Map<string, IProcessedClaim[]>();
        const patientMetaByExternalId = new Map<string, IProcessedPatient>();
    
        uniqueClaimRecords.forEach(msg => {
            const patient = this.getPatientFromMessage(msg);
            const externalId = patient?.externalId;
    
            if (!externalId) return;
    
            const providerMsg = msg as ProviderHL7Message;
            const supportMsg = msg as SupportHL7Message;
    
            const services: ServiceType[] =
                providerMsg.professional?.services ||
                providerMsg.institutional?.services ||
                supportMsg.supportProfessional?.services ||
                supportMsg.providerProfessional?.services ||
                supportMsg.supportInstitutional?.services ||
                supportMsg.providerInstitutional?.services ||
                [];
    
            const sortedServices = _.orderBy(
                services.filter(s => s && typeof s === 'object' && 'fromDateOfService' in s),
                ['fromDateOfService'],
                ['desc'],
            );
    
            const latestFromDateOfService = sortedServices[0]?.fromDateOfService || '';
    
            const controlNumber =
                providerMsg.professional?.controlNumber ||
                providerMsg.institutional?.controlNumber ||
                supportMsg.supportProfessional?.controlNumber ||
                supportMsg.providerProfessional?.controlNumber ||
                supportMsg.supportInstitutional?.controlNumber ||
                supportMsg.providerInstitutional?.controlNumber ||
                '';
    
            const claim: IProcessedClaim = {
                claimId: msg.claimId!,
                createdAt: msg.created || '',
                hl7MessageId: msg.id || '',
                dos: latestFromDateOfService,
                controlNumber,
            };
    
            if (!claimsByExternalId.has(externalId)) {
                claimsByExternalId.set(externalId, []);
            }
    
            claimsByExternalId.get(externalId)!.push(claim);
    
            // store patient meta (first occurrence wins)
            if (!patientMetaByExternalId.has(externalId)) {
                patientMetaByExternalId.set(externalId, {
                    id: patient?.patientId,
                    externalId,
                    firstName: patient?.first || '',
                    lastName: patient?.last || '',
                    dob: patient?.dob || '',
                    claims: [],
                });
            }
        });
    
        // Preserve order
        const patientOrder = _.uniq(
            uniqueClaimRecords
                .map(msg => this.getPatientFromMessage(msg)?.externalId)
                .filter(Boolean),
        );
    
        const processedPatients: IProcessedPatient[] = patientOrder
            .filter(externalId => claimsByExternalId.has(externalId!))
            .map(externalId => {
                const patientMeta = patientMetaByExternalId.get(externalId!)!;
                const claims = claimsByExternalId.get(externalId!)!;
    
                // For each patient, sort claims by DOS (latest DOS first)
                const sortedClaims = _.orderBy(claims, ['dos'], ['desc']);
    
                return {
                    ...patientMeta,
                    claims: sortedClaims,
                };
            });
    
        return processedPatients;
    };

    private readonly handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        this.props.onChange?.(value);

        if (value.trim()) {
            const updates: Partial<IAdvancedClaimSearchBarState> = {
                searchValue: value,
                currentOffset: 0,
                hasMoreResults: false,
            };

            if (this.state.popoverMode === null) {
                updates.popoverMode = 'results';
            }

            this.setState(
                updates as Pick<
                    IAdvancedClaimSearchBarState,
                    'searchValue' | 'popoverMode' | 'currentOffset' | 'hasMoreResults'
                >,
            );
            this.debouncedSearch(value);
        } else {
            this.debouncedSearch.cancel();
            this.setState(prevState => ({
                searchValue: value,
                processedResults: [],
                searchError: null,
                hasSearched: false,
                popoverMode: prevState.popoverMode === 'results' ? null : prevState.popoverMode,
                currentOffset: 0,
                hasMoreResults: false,
                isLoadingMore: false,
                currentSearchToken: prevState.currentSearchToken + 1,
            }));
        }
    };

    private readonly handleInputFocus = (): void => {
        const { popoverMode, searchValue, hasSearched, processedResults } = this.state;

        if (popoverMode === null && searchValue.trim() && (hasSearched || processedResults.length > 0)) {
            this.setState({ popoverMode: 'results' });
        }
    };

    private readonly performSearch = async (query: string): Promise<void> => {
        const filters = parseSearchQuery(query);

        if (_.isEmpty(filters)) {
            return;
        }

        // Increment search token to invalidate any in-flight requests
        this.setState(prevState => ({
            isSearching: true,
            searchError: null,
            currentOffset: 0,
            hasMoreResults: false,
            isLoadingMore: false,
            currentSearchToken: prevState.currentSearchToken + 1,
        }));

        try {
            const { isSupport } = this.props;

            const results = await this.fetchHL7Messages(filters, 0, isSupport);
            const processed = this.processSearchResults(results);

            this.setState({
                processedResults: processed,
                isSearching: false,
                searchError: null,
                hasSearched: true,
                hasMoreResults: results.length === 50,
                isLoadingMore: false,
            });
        } catch (error) {
            this.setState({
                isSearching: false,
                searchError: 'Failed to perform search. Please try again.',
                processedResults: [],
                hasSearched: true,
                hasMoreResults: false,
                isLoadingMore: false,
            });
        }
    };

    private readonly handleFilterButtonClick = (e: React.MouseEvent<Element, MouseEvent>): void => {
        this.setState({ popoverMode: 'filters' });
    };

    private readonly handlePopoverClose = (): void => {
        this.setState({ popoverMode: null });
    };

    private readonly updateStateField = <K extends keyof IAdvancedClaimSearchBarState>(
        field: K,
        value: IAdvancedClaimSearchBarState[K],
    ): void => {
        this.setState({ [field]: value } as Pick<IAdvancedClaimSearchBarState, K>);
    };

    private readonly handleFieldChange =
        (field: keyof IAdvancedClaimSearchBarState) =>
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            this.updateStateField(field, e.target.value as IAdvancedClaimSearchBarState[typeof field]);
        };

    private readonly handleDateChange =
        (field: 'fromDateOfService' | 'patientDob') =>
        (date: Date | undefined): void => {
            const dateString = date ? formatDate(date, 'yyyy-MM-dd') : '';
            this.updateStateField(field, dateString);
        };

    private readonly hasAnyFiltersSet = (): boolean => {
        const {
            patientFirstName,
            patientLastName,
            fromDateOfService,
            patientDob,
            patientExternalId,
            claimControlNumber,
            additionalId,
        } = this.state;

        return !!(
            patientFirstName ||
            patientLastName ||
            fromDateOfService ||
            patientDob ||
            patientExternalId ||
            claimControlNumber ||
            additionalId
        );
    };

    private readonly buildAdvancedFilters = (): ISearchFilters => {
        const {
            patientFirstName,
            patientLastName,
            fromDateOfService,
            patientDob,
            patientExternalId,
            claimControlNumber,
            additionalId,
        } = this.state;

        const filters: ISearchFilters = {};

        if (patientFirstName) {
            filters.filterPatientFirstName = equalsFilter(patientFirstName);
        }
        if (patientLastName) {
            filters.filterPatientLastName = equalsFilter(patientLastName);
        }

        if (fromDateOfService) {
            filters.filterFromDos = equalsFilter(fromDateOfService);
        }
        if (patientDob) {
            filters.filterPatientDob = equalsFilter(patientDob);
        }

        if (patientExternalId) {
            filters.filterPatientExternalId = equalsFilter(patientExternalId);
        }
        if (claimControlNumber) {
            filters.filterClaimControlNumber = equalsFilter(claimControlNumber);
        }
        if (additionalId) {
            filters.filterClaimId = equalsFilter(additionalId);
        }

        return filters;
    };

    private readonly handleClear = (): void => {
        this.setState(prevState => ({
            patientFirstName: '',
            patientLastName: '',
            fromDateOfService: '',
            patientDob: '',
            patientExternalId: '',
            claimControlNumber: '',
            additionalId: '',
            searchValue: '',
            processedResults: [],
            searchError: null,
            hasSearched: false,
            datePickerKey: prevState.datePickerKey + 1,
            currentOffset: 0,
            hasMoreResults: false,
            isLoadingMore: false,
            currentSearchToken: prevState.currentSearchToken + 1,
        }));
        this.props.onChange?.('');
    };

    private readonly handleSubmit = async (): Promise<void> => {
        if (!this.hasAnyFiltersSet()) {
            return;
        }

        const filters = this.buildAdvancedFilters();

        this.setState(prevState => ({
            isSearching: true,
            searchError: null,
            currentOffset: 0,
            hasMoreResults: false,
            isLoadingMore: false,
            currentSearchToken: prevState.currentSearchToken + 1,
        }));

        try {
            const { isSupport } = this.props;

            const results = await this.fetchHL7Messages(filters, 0, isSupport);
            const processed = this.processSearchResults(results);

            this.setState({
                processedResults: processed,
                isSearching: false,
                searchError: null,
                hasSearched: true,
                hasMoreResults: results.length === 50,
                isLoadingMore: false,
            });
        } catch (error) {
            this.setState({
                isSearching: false,
                searchError: 'Failed to perform search. Please try again.',
                processedResults: [],
                hasSearched: true,
                hasMoreResults: false,
                isLoadingMore: false,
            });
        }

        this.props.onSubmit?.();
    };

    private readonly handleToggleOpenInNewTab = (value: boolean): void => {
        this.setState({ openInNewTab: value });
    };

    private readonly renderSearchResults = (): React.ReactElement | null => {
        const { processedResults, isSearching, searchError, openInNewTab, isLoadingMore } = this.state;
        const { accountId } = this.props;

        return (
            <S.SearchResultsSection>
                <S.SearchResultsHeader>
                    <S.SearchResultsTitle>Search Results</S.SearchResultsTitle>
                    <S.ToggleContainer>
                        <S.ToggleLabel>Open in New Tab</S.ToggleLabel>
                        <Toggle value={openInNewTab} onChange={this.handleToggleOpenInNewTab} />
                    </S.ToggleContainer>
                </S.SearchResultsHeader>

                <S.SearchResultsContent ref={this.scrollContainerRef} onScroll={this.handleScroll}>
                    {isSearching && <S.LoadingContainer>Searching...</S.LoadingContainer>}

                    {searchError && !isSearching && <S.ErrorContainer>{searchError}</S.ErrorContainer>}

                    {!isSearching && !searchError && processedResults.length === 0 && (
                        <S.EmptyContainer>No results found</S.EmptyContainer>
                    )}

                    {!isSearching && !searchError && processedResults.length > 0 && (
                        <>
                            {processedResults.map((patient, patientIndex) => {
                                const fullName = `${patient.firstName} ${patient.lastName}`.trim();
                                const formattedDob = patient.dob ? formatDate(patient.dob, 'yyyy/MM/dd') : '';
                                const patientLink = patient.externalId
                                    ? `/${accountId}/patients/details/${patient.externalId}`
                                    : undefined;

                                return (
                                    <S.PatientCard key={`${patient.externalId}-${patientIndex}`}>
                                        <S.PatientHeader>
                                            <S.PatientInfo>
                                                <S.PatientIcon>
                                                    <Icon path={mdiAccountInjuryOutline} size={1} />
                                                </S.PatientIcon>
                                                <S.PatientNameContainer>
                                                    <S.PatientLabel>Patient</S.PatientLabel>
                                                    {patientLink ? (
                                                        <S.PatientLink
                                                            to={patientLink}
                                                            target={openInNewTab ? '_blank' : '_self'}
                                                            rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                                                            {fullName || 'Unknown'}
                                                        </S.PatientLink>
                                                    ) : (
                                                        <S.PatientName>{fullName || 'Unknown'}</S.PatientName>
                                                    )}
                                                </S.PatientNameContainer>
                                            </S.PatientInfo>
                                            <S.PatientMetadata>
                                                <S.PatientDetails>
                                                    {patient.externalId && (
                                                        <S.DetailRow>
                                                            <S.DetailLabel>ID</S.DetailLabel>
                                                            <S.DetailValue>{patient.externalId}</S.DetailValue>
                                                        </S.DetailRow>
                                                    )}
                                                    {formattedDob && (
                                                        <S.DetailRow>
                                                            <S.DetailLabel>DOB</S.DetailLabel>
                                                            <S.DetailValue>{formattedDob}</S.DetailValue>
                                                        </S.DetailRow>
                                                    )}
                                                </S.PatientDetails>
                                            </S.PatientMetadata>
                                        </S.PatientHeader>

                                        <S.ClaimsSection>
                                            <S.ClaimsHeader>
                                                <S.ClaimHeaderIcon />
                                                <S.ClaimHeaderColumns>
                                                    <S.ClaimHeaderColumn>
                                                        <p>Claim Control Number</p>
                                                    </S.ClaimHeaderColumn>
                                                    <S.ClaimHeaderColumn>
                                                        <p>DOS</p>
                                                    </S.ClaimHeaderColumn>
                                                </S.ClaimHeaderColumns>
                                            </S.ClaimsHeader>

                                            {patient.claims.map((claim, claimIndex) => {
                                                const claimLink = accountId
                                                    ? `/${accountId}/claims/${claim.claimId}`
                                                    : `/claims/${claim.claimId}`;
                                                const formattedDos = claim.dos
                                                    ? formatDate(claim.dos, 'MMM d yyyy')
                                                    : '';
                                                const displayText = claim.controlNumber || claim.claimId;

                                                return (
                                                    <S.ClaimRow key={`${claim.claimId}-${claimIndex}`}>
                                                        <S.ClaimIcon>
                                                            <Icon path={mdiFileDocumentOutline} size={1} />
                                                        </S.ClaimIcon>
                                                        <S.ClaimColumns>
                                                            <S.ClaimColumn>
                                                                <S.ClaimLink
                                                                    to={claimLink}
                                                                    target={openInNewTab ? '_blank' : '_self'}
                                                                    rel={
                                                                        openInNewTab ? 'noopener noreferrer' : undefined
                                                                    }>
                                                                    {displayText}
                                                                </S.ClaimLink>
                                                            </S.ClaimColumn>
                                                            <S.ClaimColumn>{formattedDos || '-'}</S.ClaimColumn>
                                                        </S.ClaimColumns>
                                                    </S.ClaimRow>
                                                );
                                            })}
                                        </S.ClaimsSection>
                                    </S.PatientCard>
                                );
                            })}

                            {isLoadingMore && <S.LoadingContainer>Loading more results...</S.LoadingContainer>}
                        </>
                    )}
                </S.SearchResultsContent>
            </S.SearchResultsSection>
        );
    };
}
